/**
 * @file: useQuotationCatalog.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useQuotationTemplateConfig } from '@euler/app/flows/task/pages/quotation/functions/useQuotationTemplateConfig';
import { keyOfIssue } from '@euler/app/flows/task/pages/quotation/functions/utils';
import {
  QuotationCatalog,
  QuotationIssueInfo,
  QuotationSubjectInfo,
} from '@euler/app/flows/task/pages/quotation/types/QuotationCatalog';
import { AbnormalLevel, SiteInspectionType } from '@euler/model/enum';
import { QuotationTemplateSubject } from '@euler/model/quotation';
import {
  ConstructionJob,
  CustomIssue,
  QuotationIssueMedia,
  SiteInspection,
} from '@euler/model/task-detail';
import { useServiceFactory } from '@euler/services/factory';
import { array2map } from '@euler/utils/array';
import { makeUniqueIdAlphabetic } from '@euler/utils/id';
import { useEffect, useMemo, useState } from 'react';

type TaskData = {
  preInspectionSites: SiteInspection[];
  preInspectionCustomIssues: CustomIssue[] | undefined;
  inspectionSites: SiteInspection[];
  inspectionCustomIssues: CustomIssue[] | undefined;
  jobs: ConstructionJob[];
};

export const useQuotationCatalog = () => {
  const { state, loading, error, invalidate } = useQuotationTemplateConfig();
  const { detail, asyncChildTasks } = useTaskContext();
  const { taskService } = useServiceFactory();
  const childTasks =
    asyncChildTasks.status === 'success' ? asyncChildTasks.result : undefined;

  const [data, setData] = useState<TaskData>(() => ({
    preInspectionSites: detail.preInspection.sites,
    preInspectionCustomIssues: detail.preInspection.customIssues,
    inspectionSites: detail.inspection.sites,
    inspectionCustomIssues: detail.inspection.customIssues,
    jobs: detail.construction.jobs,
  }));

  useEffect(() => {
    if (childTasks?.length) {
      Promise.all(
        childTasks.map(subTask =>
          taskService.getTaskDetail(subTask.taskNo, {
            projection: {
              preInspection: true,
              inspection: true,
              construction: true,
            },
          }),
        ),
      )
        .then(details => {
          setData({
            preInspectionSites: details.flatMap(
              x => x.response.preInspection.sites,
            ),
            preInspectionCustomIssues: details.flatMap(
              x => x.response.preInspection.customIssues ?? [],
            ),
            inspectionSites: details.flatMap(x => x.response.inspection.sites),
            inspectionCustomIssues: details.flatMap(
              x => x.response.inspection.customIssues ?? [],
            ),
            jobs: details.flatMap(x => x.response.construction.jobs),
          });
        })
        .catch(err => {
          console.error(err);
        });
    }
  }, [childTasks, taskService]);

  const catalog = useMemo(() => {
    const indexByName = new Map<string, QuotationTemplateSubject[]>();
    const indexByKeyword = new Map<string, QuotationTemplateSubject[]>();

    if (state != null) {
      for (const category of state.categories) {
        for (const group of category.groups) {
          for (const subject of group.subjects) {
            if (!indexByName.has(subject.name)) {
              indexByName.set(subject.name, []);
            }
            indexByName.get(subject.name)!.push(subject);
            if (subject.keywords?.length) {
              for (const keyword of subject.keywords) {
                if (!indexByKeyword.get(keyword)) {
                  indexByKeyword.set(keyword, []);
                }
                indexByKeyword.get(keyword)!.push(subject);
              }
            }
          }
        }
      }
    }

    const keywords = [...indexByKeyword.keys()];

    const preInspectionSiteMap = array2map(
      data.preInspectionSites,
      x => x.siteId,
    );
    const preCustomIssueMap = array2map(
      [
        ...data.preInspectionSites.flatMap(x => x.customIssues),
        ...(data.preInspectionCustomIssues ?? []),
      ],
      x => x.id,
    );

    const inspectionSiteMap = array2map(data.inspectionSites, x => x.siteId);
    const customIssueMap = array2map(
      [
        ...data.inspectionSites.flatMap(x => x.customIssues),
        ...(data.inspectionCustomIssues ?? []),
      ],
      x => x.id,
    );

    const suggestedSubjects: QuotationSubjectInfo[] = [];
    const subjectMap = new Map<string, QuotationSubjectInfo>();
    const issueMap = new Map<string, QuotationIssueInfo[]>();

    const jobNameSet = new Set(data.jobs.map(x => x.name));

    const addIssueInfo = (
      subjectInfo: QuotationSubjectInfo,
      issue: QuotationIssueInfo,
    ) => {
      issue.key = keyOfIssue(issue);
      let issueList = issueMap.get(subjectInfo.name);
      if (issueList?.some(x => keyOfIssue(x) === issue.key)) {
        return;
      }
      if (issueList == null) {
        issueList = [];
        issueMap.set(subjectInfo.name, issueList);
      }
      issueList.push(issue);
      if (!subjectInfo.issues.some(x => keyOfIssue(x) === issue.key)) {
        subjectInfo.issues.push(issue);
      }
    };

    const addSubjectInfo = (name: string, issues: QuotationIssueInfo[]) => {
      const matches = keywords.filter(keyword => name.includes(keyword));

      const addFromSubjects = (subjects: QuotationTemplateSubject[]) => {
        for (const subject of subjects) {
          let subjectInfo = subjectMap.get(subject.name);
          if (subjectInfo == null) {
            subjectInfo = {
              id: makeUniqueIdAlphabetic(),
              name: subject.name,
              pyInitial: subject.pyInitial,
              issues: [],
              items: subject.items.map(item => ({
                id: item.id,
                name: item.name,
                materials:
                  item.materials?.map(material => ({
                    id: material.id,
                    name: material.name,
                  })) ?? [],
              })),
            };
            subjectMap.set(subjectInfo.name, subjectInfo);
            suggestedSubjects.push(subjectInfo);
          }
          for (const issue of issues) {
            addIssueInfo(subjectInfo, issue);
          }
        }
      };

      if (indexByName.has(name) || !matches.length) {
        if (indexByName.has(name)) {
          const subjects = indexByName.get(name)!;
          addFromSubjects(subjects);
        } else {
          let subjectInfo = subjectMap.get(name);
          if (subjectInfo == null) {
            subjectInfo = {
              id: makeUniqueIdAlphabetic(),
              name,
              issues: [],
              items: [],
            };
            subjectMap.set(subjectInfo.name, subjectInfo);
            suggestedSubjects.push(subjectInfo);
          }
          if (!subjectInfo.items.some(x => x.name === name)) {
            subjectInfo.items.push({
              id: makeUniqueIdAlphabetic(),
              name,
              materials: [],
            });
          }
          for (const issue of issues) {
            addIssueInfo(subjectInfo, issue);
          }
        }
      } else {
        for (const keyword of matches) {
          const subjects = indexByKeyword.get(keyword);
          addFromSubjects(subjects ?? []);
        }
      }
    };

    //#region add suggested quotation subjects from construction jobs

    for (const job of data.jobs) {
      const issues: QuotationIssueInfo[] = job.details.map<QuotationIssueInfo>(
        jobDetail => {
          const customIssue: CustomIssue | undefined = jobDetail.customIssueId
            ? customIssueMap.get(jobDetail.customIssueId) ??
              preCustomIssueMap.get(jobDetail.customIssueId)
            : undefined;
          const siteInspection =
            jobDetail.siteId && !jobDetail.customIssueId
              ? inspectionSiteMap.get(jobDetail.siteId) ??
                preInspectionSiteMap.get(jobDetail.siteId)
              : undefined;
          const issue: QuotationIssueInfo = {
            id: `suggested-by-job:${job.id}:${jobDetail.id}`,
            key: '',
            inspectionType:
              siteInspection?.inspectionType ??
              customIssue?.inspectionType ??
              SiteInspectionType.Default,
            siteId: jobDetail.siteId,
            siteName: jobDetail.siteName ?? '',
            itemId: jobDetail.itemId,
            itemName: jobDetail.itemName,
            isCustomIssue:
              jobDetail.customIssueId != null && jobDetail.customIssueId > 0,
            abnormalLevel: jobDetail.abnormalLevel,
            severityLevel: jobDetail.severityLevel,
            authorId:
              siteInspection?.technicianId ?? customIssue?.technicianId ?? 0,
            authorName:
              siteInspection?.technicianName ??
              customIssue?.technicianName ??
              '',
            maintenanceAdvice: job.name,
            medias: (
              siteInspection?.items.flatMap(x => x.medias) ??
              customIssue?.medias ??
              []
            ).map<QuotationIssueMedia>(media => ({
              id: media.id,
              cid: media.cid,
              type: media.type,
              url: media.url,
              coverUrl: media.coverUrl,
              remark: media.remark,
            })),
            title: jobDetail.customIssueId
              ? jobDetail.label?.replace('自定义: ', '') ?? ''
              : jobDetail.label ?? '',
            remark: jobDetail.remark,
            defectiveClass: customIssue?.defectiveClass,
            inspectionResult: customIssue?.inspectionResult,
          };
          return issue;
        },
      );

      addSubjectInfo(job.name, issues);
    }

    //#endregion

    //#region utility function that suggests quotation subjects from inspection results
    const suggestFromInspectionResults = (
      siteInspections: SiteInspection[],
      customIssues: CustomIssue[],
    ) => {
      customIssues = [
        ...customIssues,
        ...siteInspections.flatMap(x => x.customIssues),
      ];
      for (const siteInspection of siteInspections) {
        for (const itemInspection of siteInspection.items) {
          if (
            !itemInspection.maintenanceAdvice ||
            itemInspection.abnormalLevel === AbnormalLevel.Fine ||
            // ignore subjects that have been added as construction job.
            jobNameSet.has(itemInspection.maintenanceAdvice)
          ) {
            continue;
          }
          const issue: QuotationIssueInfo = {
            id: makeUniqueIdAlphabetic(),
            key: '',
            inspectionType: siteInspection.inspectionType,
            siteId: siteInspection.siteId,
            siteName: siteInspection.name ?? '',
            itemId: itemInspection.itemId,
            itemName: itemInspection.name,
            isCustomIssue: false,
            abnormalLevel: itemInspection.abnormalLevel,
            severityLevel: itemInspection.severityLevel,
            authorId: siteInspection.technicianId,
            authorName: siteInspection.technicianName,
            maintenanceAdvice: itemInspection.maintenanceAdvice,
            medias: itemInspection.medias.map<QuotationIssueMedia>(media => ({
              id: media.id,
              cid: media.cid,
              type: media.type,
              url: media.url,
              coverUrl: media.coverUrl,
              remark: media.remark,
            })),
            title: itemInspection.label || itemInspection.optionLabel || '',
            remark: itemInspection.remark,
          };
          addSubjectInfo(itemInspection.maintenanceAdvice, [issue]);
        }
      }

      for (const customIssue of customIssues) {
        if (
          !customIssue.maintenanceAdvice ||
          customIssue.abnormalLevel === AbnormalLevel.Fine ||
          // ignore subjects that have been added as construction job.
          jobNameSet.has(customIssue.maintenanceAdvice)
        ) {
          continue;
        }

        const issue: QuotationIssueInfo = {
          id: makeUniqueIdAlphabetic(),
          key: '',
          inspectionType: customIssue.inspectionType,
          siteId: customIssue.siteId,
          siteName: customIssue.siteName ?? '',
          itemId: customIssue.itemId,
          itemName: customIssue.itemName,
          isCustomIssue: true,
          abnormalLevel: customIssue.abnormalLevel,
          severityLevel: customIssue.severityLevel,
          authorId: customIssue.technicianId,
          authorName: customIssue.technicianName,
          maintenanceAdvice: customIssue.maintenanceAdvice,
          medias: customIssue.medias.map<QuotationIssueMedia>(media => ({
            id: media.id,
            cid: media.cid,
            type: media.type,
            url: media.url,
            coverUrl: media.coverUrl,
            remark: media.remark,
          })),
          title: [
            customIssue.siteName,
            customIssue.label?.replace('自定义: ', ''),
          ].join(': '),
          remark: customIssue.description,
          defectiveClass: customIssue.defectiveClass,
          inspectionResult: customIssue.inspectionResult,
        };

        addSubjectInfo(customIssue.maintenanceAdvice, [issue]);
      }
    };
    //#endregion

    suggestFromInspectionResults(
      data.preInspectionSites,
      data.preInspectionCustomIssues ?? [],
    );

    suggestFromInspectionResults(
      data.inspectionSites,
      data.inspectionCustomIssues ?? [],
    );

    const result: QuotationCatalog = {
      templateConfig: state,
      categories: [],
      keywords,
      indexByName,
      indexByKeyword,
      subjectMap,
    };

    result.categories.push({
      id: 'suggested',
      name: '推荐',
      groups: [
        {
          id: 'suggested',
          name: '推荐报价项目',
          subjects: suggestedSubjects,
        },
      ],
    });

    if (state != null) {
      for (const category of state.categories) {
        const groups = category.groups.map(group => {
          const subjects = group.subjects.map(subject => {
            if (result.subjectMap.has(subject.name)) {
              return result.subjectMap.get(subject.name)!;
            }
            const issues = issueMap.get(subject.name) ?? [];
            const res: QuotationSubjectInfo = {
              id: subject.id,
              name: subject.name,
              pyInitial: subject.pyInitial,
              issues,
              items: subject.items.map(item => ({
                id: item.id,
                name: item.name,
                materials:
                  item.materials?.map(material => ({
                    id: material.id,
                    name: material.name,
                  })) ?? [],
              })),
            };
            subjectMap.set(res.name, res);
            return res;
          });
          return {
            id: group.id,
            name: group.name,
            subjects,
          };
        });
        result.categories.push({
          id: category.id,
          name: category.name,
          groups,
        });
      }
    }

    return result;
  }, [
    data.inspectionCustomIssues,
    data.inspectionSites,
    data.jobs,
    data.preInspectionCustomIssues,
    data.preInspectionSites,
    state,
  ]);

  return { loading, catalog, error, invalidate };
};
